import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

const QUOTES = [
  "Consistency beats motivation.",
  "Even 20 minutes today matters.",
  "You are building momentum.",
  "Small steps compound fast.",
  "Discipline creates freedom.",
  "Don’t break the chain.",
];

const STEPS = [
  "Setting up your learning space",
  "Loading your career system",
  "Almost ready",
];

const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const Wrapper = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background:
    radial-gradient(820px at 12% 10%, rgba(245, 158, 11, 0.16), transparent 58%),
    radial-gradient(860px at 88% 12%, rgba(56, 189, 248, 0.16), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Orb = styled.div`
  position: absolute;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.32;
  animation: ${float} 8s ease-in-out infinite;
  background: ${({ color }) => color};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
`;

const Center = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 560px;
  padding: 40px 24px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const Brand = styled.div`
  margin-top: 20px;
  font-size: 36px;
  font-weight: 1000;
  letter-spacing: -0.04em;
  color: #f8fafc;
`;

const SpinnerWrap = styled.div`
  position: relative;
  width: 86px;
  height: 86px;
  margin: 30px auto 0;
  display: grid;
  place-items: center;
`;

const SpinnerGlow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.22), transparent 68%);
  transform: scale(1.35);
`;

const Spinner = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #f59e0b;
  border-right-color: #38bdf8;
  animation: ${spin} 1s linear infinite;
`;

const ProgressText = styled.div`
  margin-top: 18px;
  font-size: 14px;
  font-weight: 700;
  color: #cbd5e1;
`;

const ProgressBar = styled.div`
  margin: 14px auto 0;
  width: min(260px, 100%);
  height: 7px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ step }) => (step + 1) * 33}%;
  background: linear-gradient(90deg, #f59e0b, #38bdf8, #f59e0b);
  background-size: 200% 200%;
  animation: ${shimmer} 2s linear infinite;
  transition: width 0.6s ease;
`;

const Quote = styled(motion.div)`
  margin-top: 34px;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.65;
  color: #e5e7eb;
`;

export default function MotivationLoader({ visible }) {
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!visible) return;

    const quoteTimer = setInterval(
      () => setQuoteIndex((i) => (i + 1) % QUOTES.length),
      2800
    );

    const stepTimer = setInterval(
      () => setStep((s) => Math.min(s + 1, STEPS.length - 1)),
      2000
    );

    return () => {
      clearInterval(quoteTimer);
      clearInterval(stepTimer);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <Wrapper initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Orb color="rgba(245,158,11,0.45)" top="-10%" left="-10%" />
      <Orb color="rgba(56,189,248,0.35)" top="58%" left="62%" />

      <Center>
        <Eyebrow>
          <Sparkles size={14} />
          Loading
        </Eyebrow>

        <Brand>skilll.dev</Brand>

        <SpinnerWrap>
          <SpinnerGlow />
          <Spinner />
        </SpinnerWrap>

        <ProgressText>{STEPS[step]}...</ProgressText>

        <ProgressBar>
          <ProgressFill step={step} />
        </ProgressBar>

        <AnimatePresence mode="wait">
          <Quote
            key={quoteIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.45 }}
          >
            “{QUOTES[quoteIndex]}”
          </Quote>
        </AnimatePresence>
      </Center>
    </Wrapper>
  );
}
